/// <reference lib="webworker" />

// ── Worker status reporting ─────────────────────────────────────────
//
// Handlers report connection lifecycle events (started / error) into a
// shared map keyed by handler name. The logger-worker returns the map
// to clients that send a `GetWorkersStatus` message.
import { addWarning, clearWarnings } from "@opendonationassistant/news-service";
import type { MessageListenerRegistrar, WorkerMessageEvent } from "./messaging";
import { log } from "./handlers/log";

export interface WorkerStatusMessage {
  type: "HandlerStarted" | "HandlerError";
  handler: string;
  message?: string;
  timestamp: number;
}

const statuses = new Map<string, WorkerStatusMessage>();

type StatusChangeListener = (message: WorkerStatusMessage) => void;
const statusListeners = new Set<StatusChangeListener>();

// Endpoint comes from the build-time environment, with a fallback to the
// production gateway.
const NEWS_API_ENDPOINT =
  process.env.REACT_APP_NEWS_API_ENDPOINT ?? "https://api.oda.digital";

export function onStatusChange(listener: StatusChangeListener): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

export function reportStarted(token: string, handler: string): void {
  const message: WorkerStatusMessage = {
    type: "HandlerStarted",
    handler,
    timestamp: Date.now(),
  };
  statuses.set(handler, message);
  for (const listener of statusListeners) {
    listener(message);
  }
  clearWarnings({
    baseURL: NEWS_API_ENDPOINT,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: { components: [handler] },
  });
  log("INFO", `[worker-status] ${handler} started`);
}

export function reportError(
  token: string,
  handler: string,
  message: string,
): void {
  const statusMessage: WorkerStatusMessage = {
    type: "HandlerError",
    handler,
    message,
    timestamp: Date.now(),
  };
  statuses.set(handler, statusMessage);
  for (const listener of statusListeners) {
    listener(statusMessage);
  }
  addWarning({
    baseURL: NEWS_API_ENDPOINT,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: {
      message: `Worker ${handler} error: ${message}`,
      component: handler,
      priority: "warning",
    },
  });
  log("ERROR", `[worker-status] ${handler} error: ${message}`);
}

export function removeStatuses(handler: string): void {
  statuses.delete(handler);
  log("INFO", `[worker-status] ${handler} status removed`);
}

export function getStatuses(): Map<string, WorkerStatusMessage> {
  return statuses;
}

export function register(addMessageListener: MessageListenerRegistrar): void {
  addMessageListener((event: WorkerMessageEvent) => {
    const data = event.data as Record<string, unknown> | undefined;
    if (!data || typeof data.type !== "string") return;
    if (data.type === "GetWorkersStatus") {
      event.port.postMessage({
        type: "WorkersStatus",
        statuses: getStatuses(),
      });
    } else if (
      data.type === "RemoveWorkersStatus" &&
      typeof data.handler === "string"
    ) {
      removeStatuses(data.handler);
    }
  });
}
